/**
 * Locker Service
 * API-anrop för nyckelhantering (skåp, nycklar, kopplingar).
 */

import api from '@/services/api/client';
import type {
  Cabinet, Key, CabinetGroupAssignment, VehicleKeyAssignment, LogEntry, LogSearchParams,
  CabinetCustomerAssignment, CabinetSettings,
} from '@/types/locker';

const BASE = '/api/locker';

export const lockerService = {
  // --- KeyWin proxy: Cabinets ---
  async getCabinets(): Promise<Cabinet[]> {
    const res = await api.get(`${BASE}/cabinets`);
    return res.data.data;
  },

  async getCabinet(id: string): Promise<Cabinet> {
    const res = await api.get(`${BASE}/cabinets/${encodeURIComponent(id)}`);
    return res.data.data;
  },

  async getKeysInCabinet(cabinetId: string): Promise<Key[]> {
    const res = await api.get(`${BASE}/cabinets/${encodeURIComponent(cabinetId)}/keys`);
    return res.data.data;
  },

  // --- KeyWin proxy: Keys ---
  async getKeys(params?: Record<string, string>): Promise<Key[]> {
    const res = await api.get(`${BASE}/keys`, { params });
    return res.data.data;
  },

  // --- MariaDB: Cabinet-Group assignments ---
  async getCabinetGroupAssignments(): Promise<CabinetGroupAssignment[]> {
    const res = await api.get(`${BASE}/cabinet-groups`);
    return res.data.data;
  },

  async assignCabinetToGroup(cabinetId: string, cabinetName: string, pickupLocationId: string): Promise<CabinetGroupAssignment> {
    const res = await api.post(`${BASE}/cabinet-groups`, { cabinetId, cabinetName, pickupLocationId });
    return res.data.data;
  },

  async unassignCabinetFromGroup(cabinetId: string): Promise<void> {
    await api.delete(`${BASE}/cabinet-groups/${encodeURIComponent(cabinetId)}`);
  },

  // --- MariaDB: Vehicle-Key assignments ---
  async getVehicleKeyAssignments(): Promise<VehicleKeyAssignment[]> {
    const res = await api.get(`${BASE}/vehicle-keys`);
    return res.data.data;
  },

  async getKeyAssignmentHistory(keyId: string): Promise<VehicleKeyAssignment[]> {
    const res = await api.get(`${BASE}/vehicle-keys/history/${encodeURIComponent(keyId)}`);
    return res.data.data;
  },

  async assignVehicleToKey(
    vehicleId: string, keyId: string, keyName: string, cabinetId: string, note?: string, keywinKeyId?: string
  ): Promise<VehicleKeyAssignment> {
    const res = await api.post(`${BASE}/vehicle-keys`, { vehicleId, keyId, keyName, cabinetId, note, keywinKeyId });
    return res.data.data;
  },

  async getCabinetSettings(organizationId: string): Promise<CabinetSettings | null> {
    const res = await api.get(`${BASE}/cabinet-settings/${encodeURIComponent(organizationId)}`);
    return res.data.data ?? null;
  },

  async enableCabinetSettings(organizationId: string): Promise<CabinetSettings> {
    const res = await api.post(`${BASE}/cabinet-settings/${encodeURIComponent(organizationId)}/enable`);
    return res.data.data;
  },

  async disableCabinetSettings(organizationId: string): Promise<CabinetSettings | null> {
    const res = await api.post(`${BASE}/cabinet-settings/${encodeURIComponent(organizationId)}/disable`);
    return res.data.data ?? null;
  },

  async updateSuperAdminPin(
    organizationId: string,
    body: { pin?: string; generate?: boolean }
  ): Promise<CabinetSettings> {
    const res = await api.put(
      `${BASE}/cabinet-settings/${encodeURIComponent(organizationId)}/super-admin-pin`,
      body
    );
    return res.data.data;
  },

  async getCabinetCustomerAssignments(organizationId?: string): Promise<CabinetCustomerAssignment[]> {
    const res = await api.get(`${BASE}/cabinet-customers`, {
      params: organizationId ? { organizationId } : undefined,
    });
    return res.data.data;
  },

  async getEligibleCabinetCustomers(): Promise<{ id: string; name: string }[]> {
    const res = await api.get(`${BASE}/cabinet-customers/eligible`);
    return res.data.data;
  },

  async assignCabinetToCustomer(
    cabinetId: string,
    cabinetName: string,
    organizationId: string
  ): Promise<CabinetCustomerAssignment> {
    const res = await api.post(`${BASE}/cabinet-customers`, { cabinetId, cabinetName, organizationId });
    return res.data.data;
  },

  async unassignCabinetFromCustomer(cabinetId: string): Promise<void> {
    await api.delete(`${BASE}/cabinet-customers/${encodeURIComponent(cabinetId)}`);
  },

  async syncCabinetAdminKeys(cabinetId: string): Promise<{ connected: number; keyNames: string[] }> {
    const res = await api.post(`${BASE}/cabinets/${encodeURIComponent(cabinetId)}/sync-admin-keys`);
    return res.data.data;
  },

  async unassignVehicleFromKey(keyId: string): Promise<void> {
    await api.delete(`${BASE}/vehicle-keys/${encodeURIComponent(keyId)}`);
  },

  // --- KeyWin proxy: Logs ---
  async searchLogs(params?: LogSearchParams): Promise<LogEntry[]> {
    const res = await api.get(`${BASE}/logs/search`, { params });
    return res.data.data;
  },

  async checkAlarmsExist(): Promise<boolean> {
    const res = await api.get(`${BASE}/logs/alarms/exists`);
    return res.data.data?.alarmsExist ?? false;
  },

  async acknowledgeAlarm(logId: number): Promise<void> {
    await api.post(`${BASE}/logs/alarms/${logId}/acknowledge`);
  },

  async acknowledgeAllAlarms(): Promise<void> {
    await api.post(`${BASE}/logs/alarms/acknowledge-all`);
  },
};
